import React, { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';

import { fetchMonthly } from '../../../utils/fetch';
import Layout from '../../../components/Layout';
import Loading from '../../../components/Loading';
import { genYYYYMM, getTimeSeg, sortDescending } from '../../../utils';
import LayoutViz from '../../../components/LayoutViz';
import {
    getSystemList,
    getConfig,
    getAvailableAnalysis,
    getSystemListWithConfigAndGeo,
} from '../../../utils/getPageProps';
import { ACTIVITY_PAGE, YEARLY_REPORT } from '../../../utils/static';
import { S3DataType, ShareProps } from '../../../../types/share';
import {getDynamicPageLocalePath} from '../../../utils/staticPath';

const HeatMap = dynamic(
    () => import('../../../components/HeatMap'),
    { ssr: false },
);

type Props = {
    yyyymm: ShareProps['yyyymm'],
    system: ShareProps['system']
    year: ShareProps['year'],
    month: ShareProps['month'],
    locale: ShareProps['locale'],
    config: ShareProps['config'],
    page: ShareProps['page'],
    systemList: ShareProps['systemList'],
    availableAnalysis: ShareProps['availableAnalysis'],
    seo: ShareProps['seo'],
}

const generateCurrentSelectQuery = ({
    year, month, wday, hour,
}) => `${year}_${month}_${wday}_${hour}`;

const ActivityTemplate = ({
    system, year, month, locale, config, page, systemList, availableAnalysis, seo,
}: Props) => {
    const { heatmapConfig } = config;
    const { timeList } = heatmapConfig;
    const sortedTimelist = timeList.map((i) => parseInt(i, 10)).sort(sortDescending);
    const [isMountLoaded, setIsMountLoaded] = useState(false);
    const [heatMapTimeDeal, setHeatMapTimeDeal] = useState<S3DataType['heatMapTimeDeal']>(null);
    const [geo, setGeo] = useState<S3DataType['geo']>({});
    const [heatSplitData, setHeatSplitData] = useState<S3DataType['heatSplit']>({});
    const [heatMapWdayHourly, setHeatMapWdayHourly] = useState({});
    const [currentTime, setCurrentTime] = useState<number>(sortedTimelist[0]);
    const [heatMapCurrentIndex, setHeatMapCurrentIndex] = useState<number>(0);
    const [heatMapKey, setHeatMapKey] = useState<Array<string>>(null);
    const [isSplitFetching, setIsSplitFetching] = useState(false);
    const [totalHeatData, setTotalHeatData] = useState<ShareProps['totalHeatData']>({});
    const [totalHeatDataStatus, setTotalHeatDataStatus] = useState({});
    const [totalHeatCurrentSelect, setTotalHeatCurrentSelect] = useState<ShareProps['totalHeatCurrentSelect']>({
        year,
        month: config.reportType === YEARLY_REPORT ? 'all' : `${month}`,
        wday: 'all',
        hour: 'all',
    });

    const [timeSplitFetchStatus, setTimeSplitFetchStatus] = useState(
        Object.assign({}, ...timeList.map((k) => ({ [k]: { isFetching: false, isFetched: false } }))),
    );

    const [currentSeg, setCurrentSeg] = useState(getTimeSeg(currentTime, sortedTimelist).currentSeg);

    const updateTotalHeatCurrentSelect = (value) => {
        setTotalHeatCurrentSelect({
            ...totalHeatCurrentSelect,
            ...value,
        });
    };

    const updateTime = (newTime) => {
        setCurrentTime(parseInt(newTime, 10));
        setHeatMapCurrentIndex(heatMapKey.indexOf(newTime));
    };

    const prevTick = () => {
        const index = heatMapCurrentIndex !== 0 ? heatMapCurrentIndex - 1 : heatMapCurrentIndex;
        setCurrentTime(parseInt(heatMapKey[index], 10));
        setHeatMapCurrentIndex(index);
    };

    const nextTick = () => {
        const { length } = heatMapKey;
        const index = heatMapCurrentIndex + 1 === length ? heatMapCurrentIndex : heatMapCurrentIndex + 1;
        setCurrentTime(parseInt(heatMapKey[index], 10));
        setHeatMapCurrentIndex(index);
    };

    const yyyymm = genYYYYMM(year, month);

    const fetchHeatSplit = (time) => {
        if (time && !timeSplitFetchStatus[time].isFetching && !timeSplitFetchStatus[time].isFetched) {
            setTimeSplitFetchStatus((prevState) => ({
                ...prevState,
                [time]: {
                    isFetching: true,
                    isFetched: false,
                },
            }));
            fetchMonthly({ yyyymm, system, filename: `heat_split/${time}.json` })
                .then((result) => {
                    const resultWithGeo = {};
                    // append the geo location
                    Object.keys(result).forEach((key) => {
                        const { data, max } = result[key];
                        resultWithGeo[key] = {
                            max,
                            data: data.map((item) => {
                                const geoInfo = geo[item.stationId];
                                return {
                                    ...item,
                                    lat: geoInfo.lat,
                                    lng: geoInfo.long,
                                };
                            }),
                        };
                    });
                    setTimeSplitFetchStatus((prevState) => ({
                        ...prevState,
                        [time]: {
                            isFetching: false,
                            isFetched: true,
                        },
                    }));
                    setHeatSplitData((prevState) => ({
                        ...prevState,
                        ...resultWithGeo,
                    }));
                });
        }
    };

    const fetchTotalHeatData = (query) => {
        if ((query && !totalHeatDataStatus[query]) || (!totalHeatDataStatus[query].isFetching && !totalHeatDataStatus[query].isFetched)) {
            setTotalHeatDataStatus({
                ...totalHeatDataStatus,
                [query]: {
                    isFetching: true,
                    isFetched: false,
                },
            });
            return fetchMonthly({ yyyymm, system, filename: `heat_map_detail/${query}.json` })
                .then((result) => {
                    setTotalHeatData({
                        ...totalHeatData,
                        [query]: result,
                    });
                    setTotalHeatDataStatus({
                        ...totalHeatDataStatus,
                        [query]: {
                            isFetching: false,
                            isFetched: true,
                        },
                    });
                })
                .catch((error) => {
                    console.error(error);
                });
        }
        return Promise.resolve();
    };

    // Mount action
    useEffect(() => {
        Promise.all([
            fetchMonthly({ yyyymm, system, filename: 'heat_map_time_deal.json' }),
            fetchMonthly({ yyyymm, system, filename: 'heat_map_wday_hourly.json' }),
            fetchMonthly({ yyyymm, system, filename: 'geo.json' }),
            fetchTotalHeatData(generateCurrentSelectQuery(totalHeatCurrentSelect)),
        ]).then((results) => {
            const [
                _heatMapTimeDeal,
                _heatMapWdayHourly,
                _geo,
            ] = results;
            setHeatMapTimeDeal(_heatMapTimeDeal);
            setHeatMapKey(Object.keys(_heatMapTimeDeal));
            setHeatMapWdayHourly(_heatMapWdayHourly);
            setGeo(_geo);
        }).then(() => {
            setIsMountLoaded(true);
        });
    }, []);

    useEffect(() => {
        if (isMountLoaded) {
            const seg = getTimeSeg(currentTime, sortedTimelist);
            setCurrentSeg(seg.currentSeg);
            setIsSplitFetching(true);
            Promise.all([
                fetchHeatSplit(seg.currentSeg),
                fetchHeatSplit(seg.prevSeg),
                fetchHeatSplit(seg.nextSeg),
            ]).then(() => {
                setIsSplitFetching(true);
            });
        }
    }, [currentTime, isMountLoaded]);

    return (
        <Layout locale={locale} seo={seo}>
            <LayoutViz
                page={page}
                system={system}
                locale={locale}
                config={config}
                year={year}
                month={month}
                systemList={systemList}
                availableAnalysis={availableAnalysis}
            >
                {isMountLoaded ? (
                    <HeatMap
                        locale={locale}
                        geo={geo}
                        totalHeatCurrentSelect={totalHeatCurrentSelect}
                        updateTotalHeatCurrentSelect={updateTotalHeatCurrentSelect}
                        config={config}
                        currentTime={currentTime}
                        heatMapCurrentIndex={heatMapCurrentIndex}
                        fetchTotalHeatData={fetchTotalHeatData}
                        heatMapKey={heatMapKey}
                        heatMapTimeDeal={heatMapTimeDeal}
                        heatSplitData={heatSplitData}
                        isParentLoading={timeSplitFetchStatus[currentSeg].isFetching && !timeSplitFetchStatus[currentSeg].isFetched}
                        nextTick={nextTick}
                        prevTick={prevTick}
                        timeSplitIsFetched={timeSplitFetchStatus[currentSeg].isFetched}
                        timeSplitIsFetching={timeSplitFetchStatus[currentSeg].isFetching}
                        updateTime={updateTime}
                        totalHeatData={totalHeatData}
                        totalHeatDataStatus={totalHeatDataStatus}
                    />
                ) : <Loading />}
            </LayoutViz>
        </Layout>
    );
};

export async function getStaticProps(context) {
    const { locale } = context;
    const { date, system } = context.params;
    const year = date[0];
    const month = date[1] || null;
    const yyyymm = genYYYYMM(year, month);

    const config = getConfig({
        system, year, month, locale, page: ACTIVITY_PAGE,
    });

    return {
        props: {
            yyyymm,
            systemList: getSystemList(locale),
            locale,
            system,
            year,
            month,
            availableAnalysis: getAvailableAnalysis(system),
            page: ACTIVITY_PAGE,
            config,
            seo: {
                page: ACTIVITY_PAGE,
                year,
                month,
                system,
                isYearlyReport: config.reportType === YEARLY_REPORT
            }
        },
    };
}

export async function getStaticPaths() {
    let paths = [];

    getSystemListWithConfigAndGeo().forEach(({
        system, year, month, reportType,
    }) => {
        if (reportType === YEARLY_REPORT) {
            paths = [
                ...paths,
                ...getDynamicPageLocalePath(
                    {
                        system,
                        date: [`${year}`],
                    }
                )
            ];
        } else {
            paths = [
                ...paths,
                ...getDynamicPageLocalePath(
                    {
                        system,
                        date: [`${year}`, `${month}`],
                    }
                )
            ];
        }
    });
    return {
        paths,
        fallback: false, // See the "fallback" section below
    };
}

export default ActivityTemplate;
